import { CellObject, utils, writeFile } from 'xlsx';
import { TablesExportBase } from './TablesExportBase';
import { TableExportXlsx } from './types';

const MAX_SHEET_NAME_LENGTH = 31;
const EXCEL_EPOCH = Date.UTC(1899, 11, 30);
const MS_PER_DAY = 24 * 60 * 60 * 1000;
const DEFAULT_DATE_FORMAT = 'dd/mm/yyyy';

const sanitizeSheetName = (name: string) =>
  name
    .replace(/[\\/?*[\]:]/g, ' ')
    .slice(0, MAX_SHEET_NAME_LENGTH)
    .trim() || 'Sheet';

const getUniqueSheetName = (name: string, sheetNames: string[]) => {
  const count = sheetNames.filter(i => i.toLowerCase().trim() === name.toLowerCase().trim()).length;
  if (!count) return name;
  const suffix = `_${count + 1}`;
  return `${name.slice(0, MAX_SHEET_NAME_LENGTH - suffix.length)}${suffix}`;
};

// Built from local date parts: SheetJS shifts local Date objects by the historical timezone offset.
const toExcelSerial = (date: Date) =>
  (Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    date.getMinutes(),
    date.getSeconds()
  ) -
    EXCEL_EPOCH) /
  MS_PER_DAY;

const toCell = (
  value: TableExportXlsx['rows'][number][number],
  format: NonNullable<TableExportXlsx['columnFormats']>[number],
  dateFormat: string
): CellObject | TableExportXlsx['rows'][number][number] => {
  if (value instanceof Date) {
    const z = format === 'dateTime' ? `${dateFormat} hh:mm:ss` : dateFormat;
    return { t: 'n', v: toExcelSerial(value), z };
  }
  if (format === 'percent' && typeof value === 'number') return { t: 'n', v: value, z: '0.00%' };
  return value;
};

export class TablesExportXlsx extends TablesExportBase<'XLSX'> {
  constructor() {
    super('XLSX');
  }

  export(filename: string): void {
    const wb = utils.book_new();
    const sheetNames: string[] = [];

    this.tables.forEach(
      ({
        headers,
        rows,
        sheetName,
        totals,
        columnFormats = [],
        dateFormat = DEFAULT_DATE_FORMAT,
      }) => {
        const body = totals?.length ? [...rows, totals] : rows;
        const data = [
          headers,
          ...body.map(row => row.map((value, i) => toCell(value, columnFormats[i], dateFormat))),
        ];
        const ws = utils.aoa_to_sheet(data);
        const name = sanitizeSheetName(sheetName);
        utils.book_append_sheet(wb, ws, getUniqueSheetName(name, sheetNames));
        sheetNames.push(name);
      }
    );

    writeFile(wb, `${filename}.xlsx`);
  }
}
